import { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual } from "date-fns";
import type { DateRangeValue } from "../../DateRangePicker.types";

const isDateTheCurrentMonth = (date?: Date | null) => {
  if (!date) return false;

  return (
    date.getMonth() === new Date().getMonth() &&
    date.getFullYear() === new Date().getFullYear()
  );
};

export const useDateRangeCalendar = ({
  value,
  onChange,
  disableFuture,
}: {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  disableFuture: boolean;
}) => {
  // whether the two calendars are linked
  const [isLinked, setIsLinked] = useState(true);

  // cache the initial viewed dates
  const initialViewedDates = useMemo(
    () =>
      [
        value?.[0] instanceof Date ? (value[0] as Date) : new Date(),
        value?.[1] instanceof Date ? (value[1] as Date) : new Date(),
      ] as [Date, Date],
    []
  );

  // the dates that are currently being viewed (not actually selected)
  const [viewedDates, setViewedDates] = useState<[Date, Date]>(initialViewedDates);

  // react to "value" prop changes and update the viewed dates
  useEffect(() => {
    let newFromDate = value?.[0];
    let newToDate = value?.[1];

    if (isLinked && value) {
      if (value[0] && !value[1]) {
        const endDate = new Date(value[0]);
        const isCurrentMonth = isDateTheCurrentMonth(endDate);

        if (!isCurrentMonth || !disableFuture) {
          // If only start date is selected, set end view to next month
          endDate.setMonth(endDate.getMonth() + 1);
          newToDate = endDate;
        }
      } else if (!value[0] && value[1]) {
        // If only end date is selected, set start view to previous month
        const startDate = new Date(value[1]);
        startDate.setMonth(startDate.getMonth() - 1);
        newFromDate = startDate;
      }
    }

    setViewedDates((s) => {
      // if the end date is selected but not the start date,
      // and the start date is after the end date, re-adjust the start date
      if (value?.[1] && !value?.[0] && s[0] > s[1]) {
        newFromDate = s[1];
        newFromDate.setMonth(newFromDate.getMonth() - 1);
      }
      // if the start date is selected but not the end date,
      // and the end date is before the start date, re-adjust the end date
      else if (value?.[0] && !value?.[1] && s[0] > s[1]) {
        newToDate = s[0];
        newToDate.setMonth(newToDate.getMonth() + 1);
      }

      return [newFromDate ?? s[0], newToDate ?? s[1]];
    });
  }, [value, isLinked, disableFuture]);

  // monitor date distances to unlink calendars if the distance is greater than 1 month.
  // if both values are null, set "isLinked" to true
  useEffect(() => {
    setIsLinked(!value?.[0] && !value?.[1]);

    // const startDate = new Date(value[0]);
    // const endDate = new Date(value[1]);

    // const monthDiff =
    //   (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    //   (endDate.getMonth() - startDate.getMonth());

    // if (monthDiff > 1) {
    //   setIsLinked(false);
    // }
  }, [value]);

  const resetViewedDates = useCallback(() => {
    setViewedDates(initialViewedDates);
    onChange([undefined, undefined]);
  }, [initialViewedDates, onChange]);

  const onCalendarChange = useCallback(
    (newValue: Date | null, index: number) => {
      if (!onChange || !value) return;

      const dateAlreadySelected =
        value[index] && newValue && isEqual(value[index], newValue);

      // unselect a date which is already selected, if it was re-selected
      if (dateAlreadySelected) {
        const newRange: [Date | null, Date | null] = [...value];
        newRange[index] = null;

        onChange(newRange as DateRangeValue);
        return;
      }

      const newRange: [Date | null, Date | null] = [...value];
      newRange[index] = newValue;

      // Only validate and adjust dates if we're setting a date for the first time
      // (i.e., the other date is null)

      // One date is null - enforce validation
      if (index === 0 && newRange[0] && newRange[1] && newRange[0] > newRange[1]) {
        newRange[1] = null;
      } else if (
        index === 1 &&
        newRange[0] &&
        newRange[1] &&
        newRange[1] < newRange[0]
      ) {
        newRange[0] = null;
      }

      onChange(newRange as DateRangeValue);
    },
    [onChange, value]
  );

  // only move linked calendars if none of the dates are selected
  const updateLinkedCalendars = useCallback(
    (newDate: Date, index: number, preserveYear: boolean) => {
      if (!isLinked) return;

      // if (value?.[index]) {
      //   const newRange: [Date | null, Date | null] = [...value];
      //   newRange[index] = null;

      //   onChange(newRange as DateRangeValue);
      // }

      const newDates: [Date, Date] = [...viewedDates];

      // Create normalized date preserving year if needed
      const normalizedDate = new Date(
        preserveYear ? viewedDates[index].getFullYear() : newDate.getFullYear(),
        newDate.getMonth(),
        1
      );

      newDates[index] = normalizedDate;

      if (index === 0 && !value?.[1]) {
        // If left calendar changed, set right calendar to next month
        const toDate = new Date(normalizedDate);
        const isCurrentMonth = isDateTheCurrentMonth(toDate);

        // Only add a month if disableFuture is false or we're not in the current month
        if (!disableFuture || !isCurrentMonth) {
          toDate.setMonth(toDate.getMonth() + 1);
        }
        newDates[1] = toDate;
      } else if (!value?.[0]) {
        // If right calendar changed, set left calendar to previous month
        const fromDate = new Date(normalizedDate);
        fromDate.setMonth(fromDate.getMonth() - 1);
        newDates[0] = fromDate;
      }

      setViewedDates(newDates);
    },
    [isLinked, value, viewedDates, disableFuture]
  );

  const onMonthChange = useCallback(
    (newDate: Date, index: number) => {
      updateLinkedCalendars(newDate, index, false); // preserve year when changing month
    },
    [updateLinkedCalendars]
  );

  const onYearChange = useCallback(
    (newDate: Date, index: number) => {
      updateLinkedCalendars(newDate, index, false); // use new year when changing year
    },
    [updateLinkedCalendars]
  );

  // Replace toggleIsLinked with new handler
  const onLinkClick = useCallback(() => {
    setIsLinked((prev) => !prev);
  }, []);

  return {
    onCalendarChange,
    onMonthChange,
    onYearChange,
    onLinkClick,
    viewedDates,
    isLinked,
    resetViewedDates,
  };
};
